import React, {ReactElement} from 'react';
import {IElementStaticTable, ITableColRowStyle} from '../../../../../incase-domain/elements/staticTable';
import {DeviceType} from '../../../../../incase-domain/common/DeviceType.enum';

interface ElementStaticTableProps {
    element: IElementStaticTable;
    device?: DeviceType;
}
function ElementStaticTable(props: ElementStaticTableProps): ReactElement {
    const {element, device = DeviceType.DESKTOP} = props;
    const {id, font, color, columns, rowStyles, columnStyles, borderSize, borderColor} = element;

    return (
        <table
            className="c-tableElement"
            style={{
                background: color,
                color: font.color,
                width: '100%',
                height: '100%',
                fontFamily: element.fontFamily?.name,
            }}
        >
            <tbody>
                {columns.map((column: string[], columnIndex: number) => {
                    const rowStyle = rowStyles[columnIndex] || {};

                    return (
                        <tr
                            key={`static-table-${id}-${columnIndex}`}
                            style={{
                                background: rowStyle.color,
                                color: `${rowStyle.fontColor || '#111111'}`,
                                border: `${rowStyle.borderSize || '0.0625'}em solid ${rowStyle.borderColor}`,
                            }}
                        >
                            {column.map((row, rowIndex) => {
                                const columnStyle = columnStyles[rowIndex] || {};

                                function getBorderColor(columnStyle: ITableColRowStyle, rowStyles: ITableColRowStyle): string {
                                    function isColumnBorderColorSet(): boolean {
                                        return columnStyle && !!columnStyle.borderColor && columnStyle.borderColor !== '#111111';
                                    }

                                    function isRowBorderColorSet(): boolean {
                                        return rowStyle && !!rowStyles.borderColor && rowStyles.borderColor !== '#111111';
                                    }

                                    function getRowBorderColor(): string {
                                        return rowStyles.borderColor ? `${rowStyles.borderColor}` : '#111111';
                                    }

                                    if (!(isColumnBorderColorSet() || isRowBorderColorSet())) {
                                        return borderColor?.toString() || '#111111';
                                    }

                                    return isColumnBorderColorSet() ? `${columnStyle.borderColor}` : getRowBorderColor();
                                }

                                function getBorderSize(columnStyle: ITableColRowStyle, rowStyles: ITableColRowStyle): string {
                                    const DEFAULT_BORDER_SIZE = '0.0625';
                                    function isColumnBorderSet(): boolean {
                                        return columnStyle && !!columnStyle.borderSize && columnStyle.borderSize !== 1;
                                    }

                                    function isRowBorderSet(): boolean {
                                        return rowStyle && !!rowStyles.borderSize && rowStyles.borderSize !== 1;
                                    }

                                    function getRowBorderSize(): string {
                                        return rowStyles?.borderSize ? `${rowStyles.borderSize}` : '0.0625';
                                    }

                                    if (!(isColumnBorderSet() || isRowBorderSet())) {
                                        return borderSize?.toString() || DEFAULT_BORDER_SIZE;
                                    }

                                    return isColumnBorderSet() ? `${columnStyle.borderSize}` : getRowBorderSize();
                                }

                                function hasColumnFontSize(): unknown {
                                    return columnStyle && columnStyle.fontSize;
                                }

                                function hasRowFontSize(): string | number {
                                    return rowStyle.fontSize;
                                }

                                function getFontSize(): string {
                                    if (device === DeviceType.MOBILE) {
                                        return '1em';
                                    }

                                    if (hasColumnFontSize()) {
                                        return `${columnStyle.fontSize}em`;
                                    }

                                    if (hasRowFontSize()) {
                                        return `${rowStyle.fontSize}em`;
                                    }

                                    return `${font.size}em`;
                                }

                                function getColor(columnStyle: ITableColRowStyle, rowStyles: ITableColRowStyle): string {
                                    function isColumnFontColorSet(): boolean {
                                        return columnStyle && !!columnStyle.fontColor && columnStyle.fontColor !== '#111111';
                                    }

                                    function isRowFontColorSet(): boolean {
                                        return rowStyle && !!rowStyles.fontColor && rowStyles.fontColor !== '#111111';
                                    }

                                    function getRowFontColor(): string {
                                        return rowStyles.fontColor ? `${rowStyles.fontColor}` : '#111111';
                                    }

                                    if (!(isColumnFontColorSet() || isRowFontColorSet())) {
                                        return font.color?.toString() || '#111111';
                                    }

                                    return isColumnFontColorSet() ? `${columnStyle.fontColor}` : getRowFontColor();
                                }

                                return (
                                    <td
                                        key={`static-table-cell-${id}-${columnIndex}-${rowIndex}`}
                                        style={{
                                            background: columnStyle && columnStyle.color !== '#ffffff' ? columnStyle.color : rowStyle.color,
                                            fontSize: getFontSize(),
                                            color: getColor(columnStyle, rowStyle),
                                            border: `${getBorderSize(columnStyle, rowStyle)}em solid ${getBorderColor(
                                                columnStyle,
                                                rowStyle
                                            )}`,
                                        }}
                                    >
                                        {row}
                                    </td>
                                );
                            })}
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
}

export default ElementStaticTable;
